import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { ArrowLeft, Lock, Loader2, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Mobile Reset Password — target of the mobile reset email link.
// Uses /api/v2/auth/* endpoints. Reset emails link to
// /v2/reset-password by default (desktop); we separately route the
// mobile app's deep-link pattern /mobile/reset-password here for users
// who open the link on their phone.

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MobileResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);

  const [form, setForm] = useState({ password: '', confirmPassword: '' });

  useEffect(() => {
    const validate = async () => {
      if (!token) {
        setTokenError('No reset token provided');
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API_URL}/v2/auth/validate-reset-token/${token}`);
        setTokenValid(true);
        setTokenInfo(response.data);
      } catch (error) {
        setTokenError(error.response?.data?.detail || 'Invalid or expired reset link');
      } finally {
        setLoading(false);
      }
    };
    validate();
  }, [token]);

  const validatePassword = () => {
    if (form.password.length < 8) return 'Password must be at least 8 characters';
    if (!/\d/.test(form.password)) return 'Password must contain at least one number';
    if (form.password !== form.confirmPassword) return 'Passwords do not match';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validatePassword();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/v2/auth/reset-password`, {
        token,
        new_password: form.password,
      });
      setSuccess(true);
      toast.success('Password reset successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 safe-top">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenValid && !success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 safe-top">
        <Card className="w-full max-w-md" data-testid="mobile-invalid-token-card">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-2">
              <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertCircle className="h-7 w-7 text-red-500" />
              </div>
            </div>
            <CardTitle className="text-xl">Link invalid</CardTitle>
            <CardDescription>{tokenError}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              className="w-full"
              onClick={() => navigate('/mobile/forgot-password')}
              data-testid="mobile-request-new-link-button"
            >
              Request a new link
            </Button>
            <div className="text-center">
              <Link to="/mobile/login" className="text-sm text-muted-foreground inline-flex items-center gap-1">
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 safe-top">
        <Card className="w-full max-w-md" data-testid="mobile-reset-success-card">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-2">
              <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="h-7 w-7 text-green-500" />
              </div>
            </div>
            <CardTitle className="text-xl">Password reset</CardTitle>
            <CardDescription>You can now sign in with your new password.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-3">
              <p className="text-xs text-amber-700 dark:text-amber-400 text-center">
                All active sessions and trusted devices have been signed out. Sign in again on each device.
              </p>
            </div>
            <Button
              className="w-full"
              onClick={() => navigate('/mobile/login')}
              data-testid="mobile-go-to-login-button"
            >
              Go to sign in
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="mobile-reset-password-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2">
            <div className="w-14 h-14 rounded-xl overflow-hidden">
              <img src="/equipr-logo.png" alt="EQUIPR" className="w-full h-full object-cover" />
            </div>
          </div>
          <CardTitle className="text-xl">Set a new password</CardTitle>
          <CardDescription>
            {tokenInfo?.user_name ? `Hi ${tokenInfo.user_name}` : 'Create your new password'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide">New password</Label>
              <div className="relative">
                <Input
                  data-testid="mobile-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Enter new password"
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                At least 8 characters with one number.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide">Confirm password</Label>
              <Input
                data-testid="mobile-confirm-password-input"
                type={showPassword ? 'text' : 'password'}
                value={form.confirmPassword}
                onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                placeholder="Re-enter password"
                required
              />
            </div>

            <Button type="submit" className="w-full" disabled={submitting} data-testid="mobile-reset-submit-button">
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Resetting...</>
              ) : (
                <><Lock className="h-4 w-4 mr-2" />Reset password</>
              )}
            </Button>

            <div className="pt-2 text-center">
              <Link to="/mobile/login" className="text-sm text-muted-foreground inline-flex items-center gap-1">
                <ArrowLeft className="h-4 w-4" />
                Back to sign in
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default MobileResetPassword;
